import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  RiAddLine,
  RiCloseLine,
  RiPlayLine,
  RiCodeLine,
  RiEyeLine,
  RiEyeOffLine,
  RiArrowRightLine,
  RiDeleteBin6Line,
  RiDraggable,
} from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type {
  QueryBuilderState,
  TableSchema,
  QueryBuilderTable,
  SelectedColumn,
  QueryBuilderJoin,
  WhereCondition,
  GroupByColumn,
  OrderByColumn,
  JoinType,
  WhereOperator,
  WhereLogic,
} from '@/types/query-builder';
import { generateSQL, getOperatorLabel } from '@/lib/query-builder-utils';
import { VisualCanvas } from './query-builder/visual-canvas';

interface QueryBuilderProps {
  schema: TableSchema[];
  onExecute: (sql: string) => void;
  initialQuery?: string;
}

export function QueryBuilder({ schema, onExecute, initialQuery }: QueryBuilderProps) {
  const [state, setState] = useState<QueryBuilderState>({
    tables: [],
    selectedColumns: [],
    joins: [],
    whereConditions: [],
    groupBy: [],
    having: [],
    orderBy: [],
    distinct: false,
  });

  const [showSQLPreview, setShowSQLPreview] = useState(true);
  const [selectedTableToAdd, setSelectedTableToAdd] = useState<string>('');
  const [builderMode, setBuilderMode] = useState<'form' | 'visual'>('form');
  const [visualSQL, setVisualSQL] = useState<string>('');

  const generatedSQL = builderMode === 'form' ? generateSQL(state) : visualSQL;

  // Add table to query
  const addTable = (tableName: string) => {
    if (!tableName) return;

    const tableSchema = schema.find((t) => t.name === tableName);
    if (!tableSchema) return;

    const newTable: QueryBuilderTable = {
      id: `${tableName}_${Date.now()}`,
      tableName,
      columns: tableSchema.fields,
    };

    setState((prev) => ({
      ...prev,
      tables: [...prev.tables, newTable],
    }));

    setSelectedTableToAdd('');
  };

  // Remove table from query
  const removeTable = (tableId: string) => {
    setState((prev) => ({
      ...prev,
      tables: prev.tables.filter((t) => t.id !== tableId),
      selectedColumns: prev.selectedColumns.filter((c) => c.tableId !== tableId),
      joins: prev.joins.filter((j) => j.leftTableId !== tableId && j.rightTableId !== tableId),
      whereConditions: prev.whereConditions.filter((w) => w.tableId !== tableId),
      groupBy: prev.groupBy.filter((g) => g.tableId !== tableId),
      orderBy: prev.orderBy.filter((o) => o.tableId !== tableId),
    }));
  };

  // Add column to SELECT
  const addColumn = (tableId: string, columnName: string) => {
    const table = state.tables.find((t) => t.id === tableId);
    if (!table) return;

    const newColumn: SelectedColumn = {
      id: `${tableId}_${columnName}_${Date.now()}`,
      tableId,
      tableName: table.tableName,
      columnName,
    };

    setState((prev) => ({
      ...prev,
      selectedColumns: [...prev.selectedColumns, newColumn],
    }));
  };

  // Remove column from SELECT
  const removeColumn = (columnId: string) => {
    setState((prev) => ({
      ...prev,
      selectedColumns: prev.selectedColumns.filter((c) => c.id !== columnId),
    }));
  };

  // Update column aggregation
  const updateColumnAggregation = (columnId: string, aggregation?: SelectedColumn['aggregation']) => {
    setState((prev) => ({
      ...prev,
      selectedColumns: prev.selectedColumns.map((c) =>
        c.id === columnId ? { ...c, aggregation } : c
      ),
    }));
  };

  // Update column alias
  const updateColumnAlias = (columnId: string, alias: string) => {
    setState((prev) => ({
      ...prev,
      selectedColumns: prev.selectedColumns.map((c) =>
        c.id === columnId ? { ...c, alias: alias || undefined } : c
      ),
    }));
  };

  // Add JOIN
  const addJoin = () => {
    if (state.tables.length < 2) return;

    const newJoin: QueryBuilderJoin = {
      id: `join_${Date.now()}`,
      type: 'INNER',
      leftTableId: state.tables[0].id,
      leftColumn: state.tables[0].columns[0]?.name || '',
      rightTableId: state.tables[1].id,
      rightColumn: state.tables[1].columns[0]?.name || '',
    };

    setState((prev) => ({
      ...prev,
      joins: [...prev.joins, newJoin],
    }));
  };

  // Remove JOIN
  const removeJoin = (joinId: string) => {
    setState((prev) => ({
      ...prev,
      joins: prev.joins.filter((j) => j.id !== joinId),
    }));
  };

  // Update JOIN
  const updateJoin = (joinId: string, updates: Partial<QueryBuilderJoin>) => {
    setState((prev) => ({
      ...prev,
      joins: prev.joins.map((j) => (j.id === joinId ? { ...j, ...updates } : j)),
    }));
  };

  // Add WHERE condition
  const addWhereCondition = () => {
    if (state.tables.length === 0) return;

    const newCondition: WhereCondition = {
      id: `where_${Date.now()}`,
      tableId: state.tables[0].id,
      columnName: state.tables[0].columns[0]?.name || '',
      operator: 'equals',
      value: '',
      logic: 'AND',
    };

    setState((prev) => ({
      ...prev,
      whereConditions: [...prev.whereConditions, newCondition],
    }));
  };

  // Remove WHERE condition
  const removeWhereCondition = (conditionId: string) => {
    setState((prev) => ({
      ...prev,
      whereConditions: prev.whereConditions.filter((w) => w.id !== conditionId),
    }));
  };

  // Update WHERE condition
  const updateWhereCondition = (conditionId: string, updates: Partial<WhereCondition>) => {
    setState((prev) => ({
      ...prev,
      whereConditions: prev.whereConditions.map((w) =>
        w.id === conditionId ? { ...w, ...updates } : w
      ),
    }));
  };

  // Add ORDER BY
  const addOrderBy = () => {
    if (state.tables.length === 0) return;

    const newOrderBy: OrderByColumn = {
      id: `order_${Date.now()}`,
      tableId: state.tables[0].id,
      columnName: state.tables[0].columns[0]?.name || '',
      direction: 'ASC',
    };

    setState((prev) => ({
      ...prev,
      orderBy: [...prev.orderBy, newOrderBy],
    }));
  };

  // Remove ORDER BY
  const removeOrderBy = (orderId: string) => {
    setState((prev) => ({
      ...prev,
      orderBy: prev.orderBy.filter((o) => o.id !== orderId),
    }));
  };

  // Update ORDER BY
  const updateOrderBy = (orderId: string, updates: Partial<OrderByColumn>) => {
    setState((prev) => ({
      ...prev,
      orderBy: prev.orderBy.map((o) => (o.id === orderId ? { ...o, ...updates } : o)),
    }));
  };

  // Transform schema for VisualCanvas
  const visualTables = schema.map((table) => ({
    id: table.name,
    name: table.name,
    columns: (table.fields || []).map((field) => ({
      name: field.name,
      type: field.type,
      isPrimaryKey: field.isPrimary,
      isForeignKey: field.isForeign,
    })),
  }));

  return (
    <div className="space-y-4">
      {/* SQL Preview */}
      <AnimatePresence>
        {showSQLPreview && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <RiCodeLine className="size-4" />
                  SQL Preview
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSQLPreview(false)}
                >
                  <RiEyeOffLine className="size-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg bg-muted p-4 font-mono text-sm">
                  {generatedSQL.sql || 'Add tables to start building your query...'}
                </div>
                {generatedSQL.sql && (
                  <div className="mt-3">
                    <Button onClick={() => onExecute(generatedSQL.sql)} className="w-full">
                      <RiPlayLine className="size-4 mr-2" />
                      Execute Query
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {!showSQLPreview && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSQLPreview(true)}
          className="w-full"
        >
          <RiEyeLine className="size-4 mr-2" />
          Show SQL Preview
        </Button>
      )}

      {/* Builder Mode Toggle */}
      <Tabs value={builderMode} onValueChange={(v) => setBuilderMode(v as 'form' | 'visual')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="form">Form Builder</TabsTrigger>
          <TabsTrigger value="visual">Visual Builder</TabsTrigger>
        </TabsList>

        <TabsContent value="form" className="space-y-4 mt-4">
          {/* Form builder content starts here */}

      {/* Add Table Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Tables</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Select value={selectedTableToAdd} onValueChange={setSelectedTableToAdd}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a table..." />
              </SelectTrigger>
              <SelectContent>
                {schema
                  .filter((t) => !state.tables.some((st) => st.tableName === t.name))
                  .map((table) => (
                    <SelectItem key={table.name} value={table.name}>
                      {table.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button onClick={() => addTable(selectedTableToAdd)} disabled={!selectedTableToAdd}>
              <RiAddLine className="size-4" />
            </Button>
          </div>

          {/* Selected Tables */}
          <div className="space-y-2">
            {state.tables.map((table) => (
              <div
                key={table.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{table.tableName}</Badge>
                  {table.alias && <Badge variant="outline">AS {table.alias}</Badge>}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTable(table.id)}
                >
                  <RiCloseLine className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selected Columns Section */}
      {state.tables.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Select Columns</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {state.tables.map((table) => (
              <div key={table.id} className="space-y-2">
                <Label className="text-xs text-muted-foreground">{table.tableName}</Label>
                <div className="flex flex-wrap gap-2">
                  {table.columns.map((column) => {
                    const isSelected = state.selectedColumns.some(
                      (c) => c.tableId === table.id && c.columnName === column.name
                    );

                    return (
                      <Button
                        key={column.name}
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (isSelected) {
                            const col = state.selectedColumns.find(
                              (c) => c.tableId === table.id && c.columnName === column.name
                            );
                            if (col) removeColumn(col.id);
                          } else {
                            addColumn(table.id, column.name);
                          }
                        }}
                      >
                        {column.name}
                        {column.isPrimary && ' 🔑'}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Selected Columns List with Aggregations */}
            {state.selectedColumns.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Selected ({state.selectedColumns.length})</Label>
                  {state.selectedColumns.map((column) => {
                    const table = state.tables.find((t) => t.id === column.tableId);
                    return (
                      <div
                        key={column.id}
                        className="flex items-center gap-2 p-2 rounded-lg border bg-muted/50"
                      >
                        <Badge variant="secondary" className="shrink-0">
                          {table?.tableName}.{column.columnName}
                        </Badge>

                        <Select
                          value={column.aggregation || 'none'}
                          onValueChange={(value) =>
                            updateColumnAggregation(
                              column.id,
                              value === 'none' ? undefined : (value as any)
                            )
                          }
                        >
                          <SelectTrigger className="h-8 w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Agg.</SelectItem>
                            <SelectItem value="COUNT">COUNT</SelectItem>
                            <SelectItem value="SUM">SUM</SelectItem>
                            <SelectItem value="AVG">AVG</SelectItem>
                            <SelectItem value="MIN">MIN</SelectItem>
                            <SelectItem value="MAX">MAX</SelectItem>
                            <SelectItem value="COUNT_DISTINCT">COUNT DISTINCT</SelectItem>
                          </SelectContent>
                        </Select>

                        <Input
                          placeholder="Alias"
                          value={column.alias || ''}
                          onChange={(e) => updateColumnAlias(column.id, e.target.value)}
                          className="h-8 flex-1"
                        />

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeColumn(column.id)}
                        >
                          <RiCloseLine className="size-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* JOINs Section */}
      {state.tables.length >= 2 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">Joins</CardTitle>
            <Button size="sm" onClick={addJoin}>
              <RiAddLine className="size-4 mr-2" />
              Add Join
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {state.joins.map((join) => {
              const leftTable = state.tables.find((t) => t.id === join.leftTableId);
              const rightTable = state.tables.find((t) => t.id === join.rightTableId);

              return (
                <div key={join.id} className="p-3 rounded-lg border space-y-2">
                  <div className="flex items-center justify-between">
                    <Select
                      value={join.type}
                      onValueChange={(value) => updateJoin(join.id, { type: value as JoinType })}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INNER">INNER JOIN</SelectItem>
                        <SelectItem value="LEFT">LEFT JOIN</SelectItem>
                        <SelectItem value="RIGHT">RIGHT JOIN</SelectItem>
                        <SelectItem value="FULL">FULL JOIN</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button variant="ghost" size="sm" onClick={() => removeJoin(join.id)}>
                      <RiDeleteBin6Line className="size-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                    <div className="space-y-1">
                      <Select
                        value={join.leftTableId}
                        onValueChange={(value) => updateJoin(join.id, { leftTableId: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {state.tables.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.tableName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={join.leftColumn}
                        onValueChange={(value) => updateJoin(join.id, { leftColumn: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {leftTable?.columns.map((col) => (
                            <SelectItem key={col.name} value={col.name}>
                              {col.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <RiArrowRightLine className="size-4 text-muted-foreground" />

                    <div className="space-y-1">
                      <Select
                        value={join.rightTableId}
                        onValueChange={(value) => updateJoin(join.id, { rightTableId: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {state.tables.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.tableName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={join.rightColumn}
                        onValueChange={(value) => updateJoin(join.id, { rightColumn: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {rightTable?.columns.map((col) => (
                            <SelectItem key={col.name} value={col.name}>
                              {col.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* WHERE Conditions Section */}
      {state.tables.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">Where Conditions</CardTitle>
            <Button size="sm" onClick={addWhereCondition}>
              <RiAddLine className="size-4 mr-2" />
              Add Condition
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {state.whereConditions.map((condition, index) => {
              const table = state.tables.find((t) => t.id === condition.tableId);

              return (
                <div key={condition.id} className="space-y-2">
                  {index > 0 && (
                    <Select
                      value={condition.logic}
                      onValueChange={(value) =>
                        updateWhereCondition(condition.id, { logic: value as WhereLogic })
                      }
                    >
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">AND</SelectItem>
                        <SelectItem value="OR">OR</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  <div className="p-3 rounded-lg border space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">{table?.tableName}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeWhereCondition(condition.id)}
                      >
                        <RiDeleteBin6Line className="size-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={condition.columnName}
                        onValueChange={(value) =>
                          updateWhereCondition(condition.id, { columnName: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Column" />
                        </SelectTrigger>
                        <SelectContent>
                          {table?.columns.map((col) => (
                            <SelectItem key={col.name} value={col.name}>
                              {col.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={condition.operator}
                        onValueChange={(value) =>
                          updateWhereCondition(condition.id, { operator: value as WhereOperator })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals</SelectItem>
                          <SelectItem value="not_equals">Not Equals</SelectItem>
                          <SelectItem value="greater_than">Greater Than</SelectItem>
                          <SelectItem value="less_than">Less Than</SelectItem>
                          <SelectItem value="greater_or_equal">Greater or Equal</SelectItem>
                          <SelectItem value="less_or_equal">Less or Equal</SelectItem>
                          <SelectItem value="like">Contains</SelectItem>
                          <SelectItem value="not_like">Not Contains</SelectItem>
                          <SelectItem value="is_null">Is NULL</SelectItem>
                          <SelectItem value="is_not_null">Is NOT NULL</SelectItem>
                        </SelectContent>
                      </Select>

                      {!['is_null', 'is_not_null'].includes(condition.operator) && (
                        <Input
                          placeholder="Value"
                          value={condition.value as string}
                          onChange={(e) =>
                            updateWhereCondition(condition.id, { value: e.target.value })
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ORDER BY Section */}
      {state.tables.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium">Order By</CardTitle>
            <Button size="sm" onClick={addOrderBy}>
              <RiAddLine className="size-4 mr-2" />
              Add Order
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {state.orderBy.map((order) => {
              const table = state.tables.find((t) => t.id === order.tableId);

              return (
                <div key={order.id} className="flex items-center gap-2 p-2 rounded-lg border">
                  <Select
                    value={order.tableId}
                    onValueChange={(value) => {
                      const newTable = state.tables.find((t) => t.id === value);
                      updateOrderBy(order.id, {
                        tableId: value,
                        columnName: newTable?.columns[0]?.name || '',
                      });
                    }}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {state.tables.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.tableName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={order.columnName}
                    onValueChange={(value) => updateOrderBy(order.id, { columnName: value })}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {table?.columns.map((col) => (
                        <SelectItem key={col.name} value={col.name}>
                          {col.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={order.direction}
                    onValueChange={(value) =>
                      updateOrderBy(order.id, { direction: value as 'ASC' | 'DESC' })
                    }
                  >
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ASC">ASC</SelectItem>
                      <SelectItem value="DESC">DESC</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button variant="ghost" size="sm" onClick={() => removeOrderBy(order.id)}>
                    <RiCloseLine className="size-4" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* LIMIT and OFFSET Section */}
      {state.tables.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Limit & Offset</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Limit</Label>
              <Input
                type="number"
                placeholder="No limit"
                value={state.limit || ''}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    limit: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Offset</Label>
              <Input
                type="number"
                placeholder="No offset"
                value={state.offset || ''}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    offset: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              />
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="visual" className="mt-4">
          <div className="h-[600px]">
            <VisualCanvas
              tables={visualTables}
              onQueryChange={(sql) => setVisualSQL(sql)}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
